import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  computePickerPanelCoords,
  type PickerPanelCoords,
} from '@/components/ui/pickerPopoverPosition';

export type { PickerPanelCoords };

type UsePickerPopoverOptions = {
  estimatedHeight?: number;
  minWidth?: number;
  maxWidth?: number;
};

export function usePickerPopover(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  {
    estimatedHeight = 380,
    minWidth = 280,
    maxWidth = 360,
  }: UsePickerPopoverOptions = {},
) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelCoords, setPanelCoords] = useState<PickerPanelCoords | null>(null);

  const updatePanelPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const measuredHeight = panelRef.current?.offsetHeight ?? null;
    setPanelCoords(
      computePickerPanelCoords({
        triggerRect: rect,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        estimatedHeight,
        measuredHeight,
        minWidth,
        maxWidth,
      }),
    );
  }, [estimatedHeight, maxWidth, minWidth, triggerRef]);

  useEffect(() => {
    if (!open) {
      setPanelCoords(null);
      return;
    }

    updatePanelPosition();

    const onWin = () => updatePanelPosition();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);

    let resizeObserver: ResizeObserver | undefined;
    const rafId = window.requestAnimationFrame(() => {
      updatePanelPosition();
      if (panelRef.current && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => updatePanelPosition());
        resizeObserver.observe(panelRef.current);
      }
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open, updatePanelPosition]);

  return { panelRef, panelCoords, updatePanelPosition };
}
